<template>
  <div class="p-4 sm:p-6">
    <!-- Page Header -->
    <div class="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">My Vehicles</h1>
      <button
        @click="openAddModal"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150 shadow-md hover:shadow-lg"
        :style="{ backgroundColor: brandColors.primary }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Add New Vehicle
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading && vehicles.length === 0" class="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
      <div v-for="i in 4" :key="i" class="animate-pulse">
        <div class="bg-gray-200 dark:bg-gray-800 rounded-xl h-64"></div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!loading && vehicles.length === 0"
      class="bg-white dark:bg-black rounded-xl shadow-lg p-8 sm:p-12 text-center"
    >
      <div class="max-w-md mx-auto">
        <!-- Car Icon -->
        <div
          class="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
          :style="{ backgroundColor: brandColors.primary + '20' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-10 w-10 sm:h-12 sm:w-12"
            :style="{ color: brandColors.primary }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
            <path
              d="M3 13.5L5 21h14l2-7.5M3 13.5L5 6h14l2 7.5M3 13.5h18M7 21v-3m10 3v-3"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
            />
          </svg>
        </div>

        <h2 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
          No Vehicles Added Yet
        </h2>
        <p class="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
          Start by adding your first vehicle to register tires and submit claims.
        </p>
      </div>
    </div>

    <!-- Vehicle Cards Grid -->
    <!-- Responsive grid: 1 col mobile, 2 cols tablet/desktop, 3 cols XL, 4 cols 2XL -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
      <VehicleCard
        v-for="vehicle in vehicles"
        :key="vehicle.vehicle_id"
        :vehicle="vehicle"
        @view="openViewModal"
        @edit="openEditModal"
        @delete="openDeleteModal"
      />
    </div>

    <!-- Modals -->
    <AddVehicleModal
      ref="addVehicleModalRef"
      :is-open="showAddModal"
      :loading="modalLoading"
      @close="closeAddModal"
      @save="handleAddVehicle"
    />

    <EditVehicleModal
      :is-open="showEditModal"
      :vehicle="selectedVehicle"
      :loading="modalLoading"
      @close="closeEditModal"
      @save="handleEditVehicle"
    />

    <ViewVehicleModal
      :is-open="showViewModal"
      :vehicle="selectedVehicle"
      @close="closeViewModal"
      @edit="handleEditFromView"
    />

    <DeleteConfirmationModal
      :is-open="showDeleteModal"
      :is-loading="modalLoading"
      :entity-name="`${selectedVehicle?.vehicle_make} ${selectedVehicle?.vehicle_model} (${selectedVehicle?.plate_number})`"
      entity-type="vehicle"
      @close="closeDeleteModal"
      @confirm="handleDeleteVehicle"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useVehicleStore } from '@/stores/vehicleStore';
import { useBrand } from '@/composables/brand/useBrand';
import { useToast } from 'vue-toastification';
import VehicleCard from '@/components/customer/VehicleCard.vue';
import AddVehicleModal from '@/components/customer/AddVehicleModal.vue';
import EditVehicleModal from '@/components/customer/EditVehicleModal.vue';
import ViewVehicleModal from '@/components/customer/ViewVehicleModal.vue';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue';
import type { Vehicle, VehicleFormData } from '@/types/vehicle';

const vehicleStore = useVehicleStore();
const { brandColors } = useBrand();
const toast = useToast();

// State
const showAddModal = ref(false);
const showEditModal = ref(false);
const showViewModal = ref(false);
const showDeleteModal = ref(false);
const selectedVehicle = ref<Vehicle | null>(null);
const modalLoading = ref(false);
const addVehicleModalRef = ref<InstanceType<typeof AddVehicleModal> | null>(null);

// Computed
const vehicles = computed(() => vehicleStore.vehicles);
const loading = computed(() => vehicleStore.loading);

// Lifecycle
onMounted(async () => {
  await loadVehicles();
});

// Methods
async function loadVehicles() {
  try {
    await vehicleStore.fetchVehicles();
  } catch (error) {
    toast.error('Failed to load vehicles');
  }
}

// Add Modal
function openAddModal() {
  showAddModal.value = true;
}

function closeAddModal() {
  showAddModal.value = false;
}

async function handleAddVehicle(data: VehicleFormData, photoFiles: File[], photoSides: string[]) {
  modalLoading.value = true;
  try {
    await vehicleStore.createVehicle(data, photoFiles, photoSides);
    toast.success('Vehicle added successfully');
    addVehicleModalRef.value?.resetForm(); // Reset form after successful save
    closeAddModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to add vehicle');
  } finally {
    modalLoading.value = false;
  }
}

// Edit Modal
function openEditModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  selectedVehicle.value = null;
}

async function handleEditVehicle(vehicleId: number, data: VehicleFormData, photoFiles: File[], photoSides: string[], deletedPhotoIds: number[]) {
  modalLoading.value = true;
  try {
    // Delete photos marked for deletion
    for (const photoId of deletedPhotoIds) {
      await vehicleStore.deleteVehiclePhoto(photoId);
    }

    // Update vehicle with new data and photos
    await vehicleStore.updateVehicle(vehicleId, data, photoFiles, photoSides);

    toast.success('Vehicle updated successfully');
    closeEditModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to update vehicle');
  } finally {
    modalLoading.value = false;
  }
}

// View Modal
function openViewModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showViewModal.value = true;
}

function closeViewModal() {
  showViewModal.value = false;
  selectedVehicle.value = null;
}

function handleEditFromView(vehicle: Vehicle) {
  closeViewModal();
  openEditModal(vehicle);
}

// Delete Modal
function openDeleteModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  selectedVehicle.value = null;
}

async function handleDeleteVehicle() {
  if (!selectedVehicle.value) return;

  modalLoading.value = true;
  try {
    await vehicleStore.deleteVehicle(selectedVehicle.value.vehicle_id);
    toast.success('Vehicle deleted successfully');
    closeDeleteModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to delete vehicle');
  } finally {
    modalLoading.value = false;
  }
}
</script>


